import { useState } from "react";
import { StatusBadge } from "../../components/StatusBadge";
import Layout from "../../components/Layout";
import {
  HiChartBar, HiDocumentText, HiCurrencyDollar, HiBriefcase,
  HiCalendar, HiAdjustments, HiDownload, HiEye, HiCreditCard,
  HiChevronLeft,
} from "react-icons/hi";
import { CustomSelect, CustomButton } from "../../components/FormFields";
import {
  CustomTable, CustomTableHead, CustomTableBody,
  CustomTableHeadCell, CustomTableRow, CustomTableCell,
} from "../../components/CustomTable";

/* ─── Report type definitions ───────────────────────────────────────────── */
const reportTypes = [
  { id: "payroll-summary",  name: "Payroll Summary",       description: "Monthly payroll totals with per-employee drill-down",                    icon: HiChartBar,      color: "bg-purple-100 text-purple-600",  badge: "purple"  },
  { id: "payment-details",  name: "Payment Details",        description: "Bank transfer records per employee — amount, reference and status",       icon: HiCreditCard,    color: "bg-teal-100 text-teal-600",      badge: "success" },
  { id: "tax-report",       name: "Tax Report",             description: "PAYE tax deductions per employee with mode and effective rate",            icon: HiDocumentText,  color: "bg-red-100 text-red-600",        badge: "failure" },
  { id: "epf-etf-report",   name: "EPF / ETF Report",       description: "Employee and employer EPF contributions plus ETF",                        icon: HiCurrencyDollar,color: "bg-green-100 text-green-600",    badge: "success" },
  { id: "loan-report",      name: "Loan Report",            description: "Active loans, monthly installments and outstanding balances",              icon: HiBriefcase,     color: "bg-orange-100 text-orange-600",  badge: "warning" },
  { id: "leave-report",     name: "Leave Report",           description: "Leave utilisation, balances and no-pay deduction per employee",            icon: HiCalendar,      color: "bg-blue-100 text-blue-600",      badge: "indigo"  },
  { id: "custom-report",    name: "Custom Report Builder",  description: "Build reports by selecting fields, filters and date ranges",               icon: HiAdjustments,   color: "bg-indigo-100 text-indigo-600",  badge: "indigo"  },
];

/* ─── Mock data ─────────────────────────────────────────────────────────── */
const payrollMonths = [
  { month: "March 2026",    employees: 4, totalGross: 1350000, totalDeductions: 27911, totalEpf: 92030, totalNet: 356589, status: "Draft" },
  { month: "February 2026", employees: 4, totalGross: 1320000, totalDeductions: 25800, totalEpf: 90200, totalNet: 348500, status: "Paid"  },
  { month: "January 2026",  employees: 4, totalGross: 1295000, totalDeductions: 24100, totalEpf: 89500, totalNet: 341200, status: "Paid"  },
];

const payrollDrillDown = {
  "March 2026": [
    { emp_id: "EMP-001", name: "Mr. John A. Smith",   category: "Senior Staff",    basic: 85000, allowances: 7000,  gross: 92000,  deductions: 6800,  epfEe: 7360,  epfEr: 11040, etf: 2550, net: 85200  },
    { emp_id: "EMP-002", name: "Ms. Sarah Johnson",   category: "Junior Staff",    basic: 95000, allowances: 14500, gross: 109500, deductions: 16111, epfEe: 8740,  epfEr: 13110, etf: 3040, net: 93389  },
    { emp_id: "EMP-003", name: "Dr. James R. Perera", category: "Senior Staff",    basic: 120000,allowances: 18000, gross: 138000, deductions: 0,     epfEe: 11040, epfEr: 16560, etf: 4200, net: 138000 },
    { emp_id: "EMP-004", name: "Ms. Nadia Fernando",  category: "Temporary Staff", basic: 45000, allowances: 0,     gross: 45000,  deductions: 5000,  epfEe: 0,     epfEr: 0,     etf: 0,    net: 40000  },
  ],
};

const paymentData = [
  { emp_id: "EMP-001", name: "Mr. John A. Smith",   bank: "Bank of Ceylon",    branch: "Colombo",    accountNum: "****4521", amount: 85200,  paymentDate: "2026-03-28", referenceNo: "TXN-2026-03-001", status: "Paid"    },
  { emp_id: "EMP-002", name: "Ms. Sarah Johnson",   bank: "Peoples Bank",      branch: "Kandy",      accountNum: "****8823", amount: 93389,  paymentDate: "2026-03-28", referenceNo: "TXN-2026-03-002", status: "Paid"    },
  { emp_id: "EMP-003", name: "Dr. James R. Perera", bank: "Commercial Bank",   branch: "Galle",      accountNum: "****3310", amount: 138000, paymentDate: "2026-03-28", referenceNo: "TXN-2026-03-003", status: "Paid"    },
  { emp_id: "EMP-004", name: "Ms. Nadia Fernando",  bank: "Sampath Bank",      branch: "Negombo",    accountNum: "****7745", amount: 40000,  paymentDate: "",           referenceNo: "",                status: "Pending" },
];

const bankTransferSummary = [
  { bank: "Bank of Ceylon",  transfers: 1, total: 85200  },
  { bank: "Peoples Bank",    transfers: 1, total: 93389  },
  { bank: "Commercial Bank", transfers: 1, total: 138000 },
  { bank: "Sampath Bank",    transfers: 0, total: 0      },
];

const taxData = [
  { emp_id: "EMP-001", name: "Mr. John A. Smith",   taxMode: "Mode 1",  gross: 92000,  taxableIncome: 92000,  taxDeducted: 0,    effectiveRate: 0   },
  { emp_id: "EMP-002", name: "Ms. Sarah Johnson",   taxMode: "Mode 2",  gross: 109500, taxableIncome: 109500, taxDeducted: 4311, effectiveRate: 3.9 },
  { emp_id: "EMP-003", name: "Dr. James R. Perera", taxMode: "No Tax",  gross: 138000, taxableIncome: 0,      taxDeducted: 0,    effectiveRate: 0   },
  { emp_id: "EMP-004", name: "Ms. Nadia Fernando",  taxMode: "Mode 1",  gross: 45000,  taxableIncome: 45000,  taxDeducted: 0,    effectiveRate: 0   },
];

const epfData = [
  { emp_id: "EMP-001", name: "Mr. John A. Smith",   basic: 85000,  epfEe: 7360,  epfEr: 11040, etf: 2550, totalContrib: 20950 },
  { emp_id: "EMP-002", name: "Ms. Sarah Johnson",   basic: 95000,  epfEe: 8740,  epfEr: 13110, etf: 3040, totalContrib: 24890 },
  { emp_id: "EMP-003", name: "Dr. James R. Perera", basic: 120000, epfEe: 11040, epfEr: 16560, etf: 4200, totalContrib: 31800 },
  { emp_id: "EMP-004", name: "Ms. Nadia Fernando",  basic: 45000,  epfEe: 0,     epfEr: 0,     etf: 0,    totalContrib: 0     },
];

const loanData = [
  { emp_id: "EMP-001", name: "Mr. John A. Smith",   loanType: "Personal Loan",   principal: 300000, outstanding: 150000, monthlyInstall: 6000,  completion: 50 },
  { emp_id: "EMP-002", name: "Ms. Sarah Johnson",   loanType: "Salary Advance",  principal: 50000,  outstanding: 20000,  monthlyInstall: 5000,  completion: 60 },
  { emp_id: "EMP-003", name: "Dr. James R. Perera", loanType: "Vehicle Loan",    principal: 800000, outstanding: 560000, monthlyInstall: 15000, completion: 30 },
];

const leaveData = [
  {
    emp_id: "EMP-001", name: "Mr. John A. Smith", category: "Senior Staff",
    leaves: [
      { type: "Annual Leave",  entitled: 21, used: 5,  remaining: 16, noPay: 0 },
      { type: "Sick Leave",    entitled: 14, used: 2,  remaining: 12, noPay: 0 },
      { type: "Casual Leave",  entitled: 7,  used: 1,  remaining: 6,  noPay: 0 },
      { type: "No-Pay Leave",  entitled: 0,  used: 3,  remaining: 0,  noPay: 3 },
    ],
  },
  {
    emp_id: "EMP-002", name: "Ms. Sarah Johnson", category: "Junior Staff",
    leaves: [
      { type: "Annual Leave",  entitled: 21, used: 14, remaining: 7,  noPay: 0 },
      { type: "Sick Leave",    entitled: 14, used: 7,  remaining: 7,  noPay: 0 },
      { type: "Casual Leave",  entitled: 7,  used: 3,  remaining: 4,  noPay: 0 },
      { type: "Maternity Leave",entitled:84, used: 84, remaining: 0,  noPay: 0 },
    ],
  },
  {
    emp_id: "EMP-003", name: "Dr. James R. Perera", category: "Senior Staff",
    leaves: [
      { type: "Annual Leave",  entitled: 21, used: 0,  remaining: 21, noPay: 0 },
      { type: "Sick Leave",    entitled: 14, used: 4,  remaining: 10, noPay: 0 },
    ],
  },
  {
    emp_id: "EMP-004", name: "Ms. Nadia Fernando", category: "Temporary Staff",
    leaves: [
      { type: "Casual Leave",  entitled: 5,  used: 2,  remaining: 3,  noPay: 0 },
      { type: "No-Pay Leave",  entitled: 0,  used: 0,  remaining: 0,  noPay: 0 },
    ],
  },
];

/* ─── Helpers ───────────────────────────────────────────────────────────── */
const statusColor = { Draft: "warning", Paid: "success", Approved: "indigo", Pending: "warning", Failed: "failure" };

function Rs(n) { return `Rs. ${Number(n).toLocaleString()}`; }

function SectionHeader({ title, onExport }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
      <p className="font-semibold text-gray-800 dark:text-white">{title}</p>
      {onExport && (
        <CustomButton color="purple" size="sm" onClick={onExport}>
          <HiDownload className="mr-2 h-4 w-4" /> Export CSV
        </CustomButton>
      )}
    </div>
  );
}

function StatCard({ label, value, color = "text-gray-800" }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}

/* ─── Report preview sections ────────────────────────────────────────────── */

function PayrollSummaryPreview({ month }) {
  const [drillMonth, setDrillMonth] = useState(null);
  const drillData = drillMonth ? (payrollDrillDown[drillMonth] ?? []) : [];

  if (drillMonth) {
    const totals = drillData.reduce((a, e) => ({
      gross: a.gross + e.gross, deductions: a.deductions + e.deductions,
      epf: a.epf + e.epfEe + e.epfEr + e.etf, net: a.net + e.net,
    }), { gross: 0, deductions: 0, epf: 0, net: 0 });

    return (
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          <button onClick={() => setDrillMonth(null)}
            className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 dark:text-purple-400">
            <HiChevronLeft className="h-4 w-4" /> Back
          </button>
          <p className="font-semibold text-gray-800 dark:text-white">
            Employee Breakdown — {drillMonth}
          </p>
          <StatusBadge color={statusColor[payrollMonths.find(m=>m.month===drillMonth)?.status] ?? "gray"} className="text-xs ml-auto">
            {payrollMonths.find(m=>m.month===drillMonth)?.status}
          </StatusBadge>
          <CustomButton color="purple" size="sm"><HiDownload className="mr-1 h-4 w-4" /> Export</CustomButton>
        </div>
        <div className="overflow-x-auto">
          <CustomTable hoverable>
            <CustomTableHead>
              <CustomTableRow>
                <CustomTableHeadCell>Employee</CustomTableHeadCell>
                <CustomTableHeadCell>Basic</CustomTableHeadCell>
                <CustomTableHeadCell>Allowances</CustomTableHeadCell>
                <CustomTableHeadCell>Gross</CustomTableHeadCell>
                <CustomTableHeadCell>Deductions</CustomTableHeadCell>
                <CustomTableHeadCell>EPF (Ee)</CustomTableHeadCell>
                <CustomTableHeadCell>EPF (Er)</CustomTableHeadCell>
                <CustomTableHeadCell>ETF</CustomTableHeadCell>
                <CustomTableHeadCell className="text-green-600">Net Pay</CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
              {drillData.map((e) => (
                <CustomTableRow key={e.emp_id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                  <CustomTableCell>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{e.name}</p>
                    <div className="mt-0.5 flex gap-1">
                      <StatusBadge color="indigo" className="text-xs">{e.emp_id}</StatusBadge>
                      <StatusBadge color="purple" className="text-xs">{e.category}</StatusBadge>
                    </div>
                  </CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-600 dark:text-gray-300">{e.basic.toLocaleString()}</CustomTableCell>
                  <CustomTableCell className="text-sm text-green-600">{e.allowances.toLocaleString()}</CustomTableCell>
                  <CustomTableCell className="text-sm font-semibold text-gray-700 dark:text-gray-200">{e.gross.toLocaleString()}</CustomTableCell>
                  <CustomTableCell className="text-sm text-red-500">{e.deductions.toLocaleString()}</CustomTableCell>
                  <CustomTableCell className="text-sm text-orange-500">{e.epfEe.toLocaleString()}</CustomTableCell>
                  <CustomTableCell className="text-sm text-orange-400">{e.epfEr.toLocaleString()}</CustomTableCell>
                  <CustomTableCell className="text-sm text-orange-300">{e.etf.toLocaleString()}</CustomTableCell>
                  <CustomTableCell className="text-sm font-bold text-green-600">{Rs(e.net)}</CustomTableCell>
                </CustomTableRow>
              ))}
            </CustomTableBody>
          </CustomTable>
        </div>
        <div className="grid grid-cols-2 gap-4 border-t border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/50 sm:grid-cols-4">
          <StatCard label="Total Gross"       value={Rs(totals.gross)}       color="text-gray-800 dark:text-gray-100" />
          <StatCard label="Total Deductions"  value={Rs(totals.deductions)}  color="text-red-600"  />
          <StatCard label="Total EPF + ETF"   value={Rs(totals.epf)}         color="text-orange-600" />
          <StatCard label="Total Net Payable" value={Rs(totals.net)}         color="text-green-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <SectionHeader title={`Payroll Summary Report — ${month}`} onExport={() => {}} />
      <div className="overflow-x-auto">
        <CustomTable hoverable>
          <CustomTableHead>
            <CustomTableRow>
              <CustomTableHeadCell>Month</CustomTableHeadCell>
              <CustomTableHeadCell className="text-center">Employees</CustomTableHeadCell>
              <CustomTableHeadCell>Total Gross</CustomTableHeadCell>
              <CustomTableHeadCell>Total Deductions</CustomTableHeadCell>
              <CustomTableHeadCell>Total EPF/ETF</CustomTableHeadCell>
              <CustomTableHeadCell className="text-green-600">Total Net</CustomTableHeadCell>
              <CustomTableHeadCell>Status</CustomTableHeadCell>
              <CustomTableHeadCell className="text-center">Details</CustomTableHeadCell>
            </CustomTableRow>
          </CustomTableHead>
          <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
            {payrollMonths.map((p) => (
              <CustomTableRow key={p.month} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                <CustomTableCell className="font-medium text-gray-800 dark:text-gray-200">{p.month}</CustomTableCell>
                <CustomTableCell className="text-center text-sm text-gray-600 dark:text-gray-300">{p.employees}</CustomTableCell>
                <CustomTableCell className="text-sm font-semibold text-gray-700 dark:text-gray-200">{Rs(p.totalGross)}</CustomTableCell>
                <CustomTableCell className="text-sm text-red-500">{Rs(p.totalDeductions)}</CustomTableCell>
                <CustomTableCell className="text-sm text-orange-500">{Rs(p.totalEpf)}</CustomTableCell>
                <CustomTableCell className="text-sm font-bold text-green-600">{Rs(p.totalNet)}</CustomTableCell>
                <CustomTableCell>
                  <StatusBadge color={statusColor[p.status]} className="w-fit text-xs">{p.status}</StatusBadge>
                </CustomTableCell>
                <CustomTableCell className="text-center">
                  <CustomButton size="xs" color="purple" outline
                    onClick={() => setDrillMonth(p.month)}
                    disabled={!payrollDrillDown[p.month]}>
                    <HiEye className="mr-1 h-3.5 w-3.5" /> View
                  </CustomButton>
                </CustomTableCell>
              </CustomTableRow>
            ))}
          </CustomTableBody>
        </CustomTable>
      </div>
    </div>
  );
}

function PaymentDetailsPreview({ month }) {
  const paid    = paymentData.filter(p => p.status === "Paid").reduce((a, p) => a + p.amount, 0);
  const pending = paymentData.filter(p => p.status === "Pending").reduce((a, p) => a + p.amount, 0);

  return (
    <div className="flex flex-col gap-5">
      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total Employees"  value={paymentData.length} />
        <StatCard label="Paid"             value={paymentData.filter(p=>p.status==="Paid").length}    color="text-green-600" />
        <StatCard label="Pending"          value={paymentData.filter(p=>p.status==="Pending").length} color="text-yellow-600" />
        <StatCard label="Total Paid Out"   value={Rs(paid)}           color="text-green-600" />
      </div>

      {/* Per-employee payment records */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <SectionHeader title={`Payment Details — ${month}`} onExport={() => {}} />
        <div className="overflow-x-auto">
          <CustomTable hoverable>
            <CustomTableHead>
              <CustomTableRow>
                <CustomTableHeadCell>Employee</CustomTableHeadCell>
                <CustomTableHeadCell>Bank</CustomTableHeadCell>
                <CustomTableHeadCell>Branch</CustomTableHeadCell>
                <CustomTableHeadCell>Account</CustomTableHeadCell>
                <CustomTableHeadCell>Amount</CustomTableHeadCell>
                <CustomTableHeadCell>Payment Date</CustomTableHeadCell>
                <CustomTableHeadCell>Reference No.</CustomTableHeadCell>
                <CustomTableHeadCell>Status</CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paymentData.map((p) => (
                <CustomTableRow key={p.emp_id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                  <CustomTableCell>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{p.name}</p>
                    <p className="font-mono text-xs text-purple-500">{p.emp_id}</p>
                  </CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-600 dark:text-gray-300">{p.bank}</CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-500 dark:text-gray-400">{p.branch}</CustomTableCell>
                  <CustomTableCell className="font-mono text-sm text-gray-600 dark:text-gray-300">{p.accountNum}</CustomTableCell>
                  <CustomTableCell className="text-sm font-semibold text-green-600">{Rs(p.amount)}</CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-500 dark:text-gray-400">
                    {p.paymentDate || <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </CustomTableCell>
                  <CustomTableCell className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    {p.referenceNo || <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </CustomTableCell>
                  <CustomTableCell>
                    <StatusBadge color={statusColor[p.status]} className="w-fit text-xs">{p.status}</StatusBadge>
                  </CustomTableCell>
                </CustomTableRow>
              ))}
            </CustomTableBody>
          </CustomTable>
        </div>
      </div>

      {/* Bank transfer summary */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <SectionHeader title="Bank Transfer Summary" />
        <div className="overflow-x-auto">
          <CustomTable hoverable>
            <CustomTableHead>
              <CustomTableRow>
                <CustomTableHeadCell>Bank</CustomTableHeadCell>
                <CustomTableHeadCell className="text-center">Transfers</CustomTableHeadCell>
                <CustomTableHeadCell>Total Amount</CustomTableHeadCell>
                <CustomTableHeadCell>Status</CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
              {bankTransferSummary.map((b) => (
                <CustomTableRow key={b.bank} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                  <CustomTableCell className="text-sm font-medium text-gray-800 dark:text-gray-200">{b.bank}</CustomTableCell>
                  <CustomTableCell className="text-center text-sm text-gray-600 dark:text-gray-300">{b.transfers}</CustomTableCell>
                  <CustomTableCell className="text-sm font-semibold text-green-600">{Rs(b.total)}</CustomTableCell>
                  <CustomTableCell>
                    <StatusBadge color={b.transfers > 0 ? "success" : "warning"} className="w-fit text-xs">
                      {b.transfers > 0 ? "Completed" : "Pending"}
                    </StatusBadge>
                  </CustomTableCell>
                </CustomTableRow>
              ))}
            </CustomTableBody>
          </CustomTable>
        </div>
      </div>
    </div>
  );
}

function TaxReportPreview({ month }) {
  const totalTax = taxData.reduce((a, e) => a + e.taxDeducted, 0);
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label="Employees Taxed"    value={taxData.filter(e=>e.taxDeducted>0).length} />
        <StatCard label="Employees Tax-Free" value={taxData.filter(e=>e.taxDeducted===0).length} color="text-green-600" />
        <StatCard label="Total PAYE This Month" value={Rs(totalTax)} color="text-red-600" />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <SectionHeader title={`Tax Report — ${month}`} onExport={() => {}} />
        <div className="overflow-x-auto">
          <CustomTable hoverable>
            <CustomTableHead>
              <CustomTableRow>
                <CustomTableHeadCell>Employee</CustomTableHeadCell>
                <CustomTableHeadCell>Tax Mode</CustomTableHeadCell>
                <CustomTableHeadCell>Gross Income</CustomTableHeadCell>
                <CustomTableHeadCell>Taxable Income</CustomTableHeadCell>
                <CustomTableHeadCell>Tax Deducted</CustomTableHeadCell>
                <CustomTableHeadCell>Effective Rate</CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
              {taxData.map((e) => (
                <CustomTableRow key={e.emp_id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                  <CustomTableCell>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{e.name}</p>
                    <p className="font-mono text-xs text-purple-500">{e.emp_id}</p>
                  </CustomTableCell>
                  <CustomTableCell>
                    <StatusBadge color={e.taxMode === "No Tax" ? "gray" : "warning"} className="text-xs w-fit">
                      {e.taxMode}
                    </StatusBadge>
                  </CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-600 dark:text-gray-300">{Rs(e.gross)}</CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-600 dark:text-gray-300">
                    {e.taxableIncome === 0 ? <span className="text-gray-400">Exempt</span> : Rs(e.taxableIncome)}
                  </CustomTableCell>
                  <CustomTableCell className={`text-sm font-semibold ${e.taxDeducted > 0 ? "text-red-500" : "text-gray-400"}`}>
                    {Rs(e.taxDeducted)}
                  </CustomTableCell>
                  <CustomTableCell>
                    <StatusBadge color={e.effectiveRate === 0 ? "gray" : "failure"} className="text-xs w-fit">
                      {e.effectiveRate}%
                    </StatusBadge>
                  </CustomTableCell>
                </CustomTableRow>
              ))}
            </CustomTableBody>
          </CustomTable>
        </div>
      </div>
    </div>
  );
}

function EpfEtfReportPreview({ month }) {
  const totals = epfData.reduce((a, e) => ({
    epfEe: a.epfEe + e.epfEe, epfEr: a.epfEr + e.epfEr, etf: a.etf + e.etf, total: a.total + e.totalContrib,
  }), { epfEe: 0, epfEr: 0, etf: 0, total: 0 });

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="EPF Employee (8%)" value={Rs(totals.epfEe)} color="text-orange-600" />
        <StatCard label="EPF Employer (12%)"value={Rs(totals.epfEr)} color="text-orange-500" />
        <StatCard label="ETF (3%)"           value={Rs(totals.etf)}   color="text-orange-400" />
        <StatCard label="Total Contributions"value={Rs(totals.total)} color="text-purple-600" />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <SectionHeader title={`EPF / ETF Report — ${month}`} onExport={() => {}} />
        <div className="overflow-x-auto">
          <CustomTable hoverable>
            <CustomTableHead>
              <CustomTableRow>
                <CustomTableHeadCell>Employee</CustomTableHeadCell>
                <CustomTableHeadCell>Basic Salary</CustomTableHeadCell>
                <CustomTableHeadCell>EPF Employee (8%)</CustomTableHeadCell>
                <CustomTableHeadCell>EPF Employer (12%)</CustomTableHeadCell>
                <CustomTableHeadCell>ETF (3%)</CustomTableHeadCell>
                <CustomTableHeadCell>Total Contribution</CustomTableHeadCell>
                <CustomTableHeadCell>EPF Status</CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
              {epfData.map((e) => (
                <CustomTableRow key={e.emp_id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                  <CustomTableCell>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{e.name}</p>
                    <p className="font-mono text-xs text-purple-500">{e.emp_id}</p>
                  </CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-600 dark:text-gray-300">{Rs(e.basic)}</CustomTableCell>
                  <CustomTableCell className="text-sm font-medium text-orange-600">{Rs(e.epfEe)}</CustomTableCell>
                  <CustomTableCell className="text-sm font-medium text-orange-500">{Rs(e.epfEr)}</CustomTableCell>
                  <CustomTableCell className="text-sm font-medium text-orange-400">{Rs(e.etf)}</CustomTableCell>
                  <CustomTableCell className="text-sm font-bold text-purple-600">{Rs(e.totalContrib)}</CustomTableCell>
                  <CustomTableCell>
                    <StatusBadge color={e.epfEe > 0 ? "success" : "gray"} className="w-fit text-xs">
                      {e.epfEe > 0 ? "Applicable" : "Exempt"}
                    </StatusBadge>
                  </CustomTableCell>
                </CustomTableRow>
              ))}
            </CustomTableBody>
          </CustomTable>
        </div>
      </div>
    </div>
  );
}

function LoanReportPreview({ month }) {
  const totalOutstanding = loanData.reduce((a, e) => a + e.outstanding, 0);
  const totalRecovery    = loanData.reduce((a, e) => a + e.monthlyInstall, 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label="Active Loans"         value={loanData.length} />
        <StatCard label="Total Outstanding"    value={Rs(totalOutstanding)} color="text-orange-600" />
        <StatCard label="Monthly Recovery"     value={Rs(totalRecovery)}    color="text-green-600" />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <SectionHeader title={`Loan Report — ${month}`} onExport={() => {}} />
        <div className="overflow-x-auto">
          <CustomTable hoverable>
            <CustomTableHead>
              <CustomTableRow>
                <CustomTableHeadCell>Employee</CustomTableHeadCell>
                <CustomTableHeadCell>Loan Type</CustomTableHeadCell>
                <CustomTableHeadCell>Principal</CustomTableHeadCell>
                <CustomTableHeadCell>Outstanding</CustomTableHeadCell>
                <CustomTableHeadCell>Monthly Installment</CustomTableHeadCell>
                <CustomTableHeadCell>Completion</CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loanData.map((e) => (
                <CustomTableRow key={e.emp_id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                  <CustomTableCell>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{e.name}</p>
                    <p className="font-mono text-xs text-purple-500">{e.emp_id}</p>
                  </CustomTableCell>
                  <CustomTableCell>
                    <StatusBadge color="warning" className="w-fit text-xs">{e.loanType}</StatusBadge>
                  </CustomTableCell>
                  <CustomTableCell className="text-sm text-gray-600 dark:text-gray-300">{Rs(e.principal)}</CustomTableCell>
                  <CustomTableCell className="text-sm font-semibold text-orange-600">{Rs(e.outstanding)}</CustomTableCell>
                  <CustomTableCell className="text-sm font-medium text-green-600">{Rs(e.monthlyInstall)}</CustomTableCell>
                  <CustomTableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-2 rounded-full ${e.completion >= 75 ? "bg-green-500" : e.completion >= 40 ? "bg-yellow-400" : "bg-orange-400"}`}
                          style={{ width: `${e.completion}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{e.completion}%</span>
                    </div>
                  </CustomTableCell>
                </CustomTableRow>
              ))}
            </CustomTableBody>
          </CustomTable>
        </div>
      </div>
    </div>
  );
}

function LeaveReportPreview({ month }) {
  const [selectedEmp, setSelectedEmp] = useState(leaveData[0]);

  return (
    <div className="flex flex-col gap-5">
      {/* Employee selector */}
      <div className="flex flex-wrap gap-2">
        {leaveData.map((e) => (
          <button key={e.emp_id} type="button" onClick={() => setSelectedEmp(e)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              selectedEmp?.emp_id === e.emp_id
                ? "border-purple-500 bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                : "border-gray-200 text-gray-500 hover:border-purple-300 dark:border-gray-600 dark:text-gray-400"
            }`}>
            {e.name.split(" ").slice(-1)[0]}
          </button>
        ))}
      </div>

      {selectedEmp && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
            <div>
              <p className="font-semibold text-gray-800 dark:text-white">{selectedEmp.name}</p>
              <p className="text-xs text-gray-400">{selectedEmp.emp_id} · {selectedEmp.category}</p>
            </div>
            <CustomButton color="purple" size="sm"><HiDownload className="mr-1 h-4 w-4" /> Export</CustomButton>
          </div>
          <div className="overflow-x-auto">
            <CustomTable hoverable>
              <CustomTableHead>
                <CustomTableRow>
                  <CustomTableHeadCell>Leave Type</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-center">Entitled</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-center">Used</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-center">Remaining</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-center">No-Pay Days</CustomTableHeadCell>
                  <CustomTableHeadCell>Status</CustomTableHeadCell>
                </CustomTableRow>
              </CustomTableHead>
              <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
                {selectedEmp.leaves.map((l) => {
                  const pct = l.entitled > 0 ? l.used / l.entitled : 0;
                  const color = l.entitled === 0 ? "gray" : pct >= 1 ? "failure" : pct >= 0.75 ? "warning" : "success";
                  const label = l.entitled === 0 ? "Unlimited" : pct >= 1 ? "Exhausted" : pct >= 0.75 ? "Low" : "Available";
                  return (
                    <CustomTableRow key={l.type} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                      <CustomTableCell className="text-sm font-medium text-gray-800 dark:text-gray-200">{l.type}</CustomTableCell>
                      <CustomTableCell className="text-center text-sm text-gray-500">{l.entitled === 0 ? "∞" : l.entitled}</CustomTableCell>
                      <CustomTableCell className="text-center text-sm text-gray-500">{l.used}</CustomTableCell>
                      <CustomTableCell className="text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                        {l.entitled === 0 ? "∞" : Math.max(0, l.remaining)}
                      </CustomTableCell>
                      <CustomTableCell className="text-center">
                        {l.noPay > 0
                          ? <StatusBadge color="failure" className="text-xs">{l.noPay} days</StatusBadge>
                          : <span className="text-xs text-gray-400">—</span>}
                      </CustomTableCell>
                      <CustomTableCell>
                        <StatusBadge color={color} className="text-xs w-fit">{label}</StatusBadge>
                      </CustomTableCell>
                    </CustomTableRow>
                  );
                })}
              </CustomTableBody>
            </CustomTable>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomReportPreview() {
  const [reportType, setReportType] = useState("Payroll");
  const [groupBy, setGroupBy]       = useState("Employee");
  const [catFilter, setCatFilter]   = useState("All");
  const [generated, setGenerated]   = useState(false);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <p className="mb-4 font-semibold text-gray-800 dark:text-white">Custom Report Builder</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Report Type</label>
          <CustomSelect value={reportType} onChange={(e) => { setReportType(e.target.value); setGenerated(false); }}>
            <option>Payroll</option><option>Leave</option><option>Loan</option><option>EPF/ETF</option><option>Tax</option>
          </CustomSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Group By</label>
          <CustomSelect value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option>Employee</option><option>Category</option><option>Month</option>
          </CustomSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Category Filter</label>
          <CustomSelect value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
            <option>All</option><option>Senior Staff</option><option>Junior Staff</option>
            <option>Temporary Staff</option><option>Intern</option>
          </CustomSelect>
        </div>
      </div>
      <div className="mt-4 flex gap-3">
        <CustomButton color="purple" onClick={() => setGenerated(true)}>
          <HiEye className="mr-2 h-4 w-4" /> Generate Report
        </CustomButton>
        <CustomButton color="gray" outline><HiDownload className="mr-2 h-4 w-4" /> Export</CustomButton>
      </div>
      {generated && (
        <div className="mt-5 rounded-lg border border-purple-100 bg-purple-50 p-4 dark:border-purple-800/40 dark:bg-purple-900/10">
          <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
            Generated: {reportType} Report — Grouped by {groupBy}
            {catFilter !== "All" && ` — Category: ${catFilter}`}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Connect to backend API to return live data based on these filters.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Main Reports Page ──────────────────────────────────────────────────── */
export default function Reports() {
  const [selectedReport, setSelectedReport] = useState(null);
  const [month, setMonth]                   = useState("2026-03");

  const displayMonth = new Date(month + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" });

  function renderPreview() {
    switch (selectedReport) {
      case "payroll-summary": return <PayrollSummaryPreview month={displayMonth} />;
      case "payment-details": return <PaymentDetailsPreview month={displayMonth} />;
      case "tax-report":      return <TaxReportPreview      month={displayMonth} />;
      case "epf-etf-report":  return <EpfEtfReportPreview   month={displayMonth} />;
      case "loan-report":     return <LoanReportPreview      month={displayMonth} />;
      case "leave-report":    return <LeaveReportPreview     month={displayMonth} />;
      case "custom-report":   return <CustomReportPreview />;
      default: return null;
    }
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Reports</h1>
            <p className="mt-1 text-sm text-gray-500">Generate, view and download payroll reports</p>
          </div>
          <input
            type="month" value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
          />
        </div>

        {/* Report Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {reportTypes.map((r) => {
            const Icon = r.icon;
            const active = selectedReport === r.id;
            return (
              <div
                key={r.id}
                onClick={() => setSelectedReport(active ? null : r.id)}
                className={`cursor-pointer rounded-xl border p-5 shadow-sm transition-all hover:shadow-md ${
                  active
                    ? "border-purple-400 bg-purple-50 dark:border-purple-600 dark:bg-purple-900/20"
                    : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${r.color}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{r.name}</p>
                      {active && <StatusBadge color="purple" className="text-xs">Selected</StatusBadge>}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{r.description}</p>
                    <div className="mt-3 flex gap-2">
                      <CustomButton size="xs" color="purple" outline onClick={(e) => { e.stopPropagation(); setSelectedReport(r.id); }}>
                        <HiEye className="mr-1 h-3.5 w-3.5" /> View
                      </CustomButton>
                      <CustomButton size="xs" color="gray" outline onClick={(e) => e.stopPropagation()}>
                        <HiDownload className="mr-1 h-3.5 w-3.5" /> Export
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Report Preview */}
        {renderPreview()}

      </div>
    </Layout>
  );
}
